import type { ReactNode } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import NotFoundPage from '@/pages/NotFoundPage';

import { useLatestRelease } from './use-latest-release';
import { useDetectPlatform } from './use-platform-detect';
import { ComingSoon } from './variants/ComingSoon';
import { DetectingPlatform } from './variants/DetectingPlatform';
import { IntelNotSupported } from './variants/IntelNotSupported';
import { NonMacPreview } from './variants/NonMacPreview';
import { ReadyToDownload } from './variants/ReadyToDownload';
import { ReleaseUnavailable } from './variants/ReleaseUnavailable';
import { WindowsComingLater } from './variants/WindowsComingLater';

const PAGE_TITLE_ID = 'desktop-install-title';

interface InstallLayoutProps {
  children: ReactNode;
}

function InstallLayout({ children }: InstallLayoutProps) {
  return (
    <main
      aria-labelledby={PAGE_TITLE_ID}
      className='flex min-h-dvh flex-col items-center bg-[var(--color-bg-primary)] px-6 py-16'
    >
      <div className='w-full max-w-2xl'>
        <header className='mb-12 text-center'>
          <p className='mb-2 text-xs font-semibold uppercase tracking-widest text-[var(--color-text-secondary)]'>
            Ahoy
          </p>
          <h1
            id={PAGE_TITLE_ID}
            className='font-display text-5xl font-medium text-[var(--color-text-primary)]'
          >
            Desktop
          </h1>
          <div className='mx-auto mt-4 h-px w-16 bg-[var(--color-brand-600)]' />
        </header>
        {children}
      </div>
    </main>
  );
}

export default function DesktopInstallPage() {
  const { currentOrg } = useOrg();
  const isDesktopAppEnabled = useFeatureFlag('ahoy_desktop_app');
  const platform = useDetectPlatform();
  // Gate the latest-release fetch so it only fires when the page would actually
  // render the download CTA — the result is unused on every other platform and
  // when the feature flag is off.
  const shouldFetchRelease =
    isDesktopAppEnabled && platform.kind === 'apple_silicon_mac';
  const {
    data: releaseData,
    isLoading: isReleaseLoading,
    isError: isReleaseError,
  } = useLatestRelease(currentOrg?.id, shouldFetchRelease);

  // Feature flag gate — all hooks must be called before this early return
  if (!isDesktopAppEnabled) {
    return <NotFoundPage />;
  }

  let content: ReactNode;

  if (platform.kind === 'detecting' || !currentOrg) {
    content = <DetectingPlatform />;
  } else if (platform.kind === 'apple_silicon_mac') {
    if (isReleaseLoading) {
      content = <DetectingPlatform />;
    } else if (isReleaseError) {
      // Distinguish a failed fetch from a genuinely-unpublished release. The
      // backend returns `release: null` when no DMG is published yet (Coming
      // Soon); errors here mean we don't know — surface that instead of
      // hiding an available download behind the wrong state.
      content = <ReleaseUnavailable />;
    } else {
      const release = releaseData?.release ?? null;
      content = release ? (
        <ReadyToDownload release={release} />
      ) : (
        <ComingSoon />
      );
    }
  } else if (platform.kind === 'intel_mac') {
    content = <IntelNotSupported />;
  } else if (platform.kind === 'windows') {
    content = <WindowsComingLater />;
  } else {
    content = <NonMacPreview />;
  }

  return <InstallLayout>{content}</InstallLayout>;
}
